import { useCallback, useEffect, useState } from 'react';
import useSMSChat from 'hooks/useSMSChat';
import { Customer } from 'data/firestore/agent/customer/types';
import { EntityGet } from 'data/firestore/types';
import useTwilioConversation from 'hooks/useTwilioConversation';
import EmptyMessage from 'components/EmptyMessage';
import {
    EmptyMessageContainer, EmptyMessageEmoji, EmptyMessageTitle,
} from './styled';
import SMSChat from './chat';
import SMSChatLayout from './layout';
import ConversationsList from './ConversationList';
import { SmsDraft } from './types';

const SMSChatModal = () => {
    const { isOpen, data: customerData, closeChat } = useSMSChat();
    const { isSMSEnabled } = useTwilioConversation();
    const [activeCustomer, setActiveCustomer] = useState<EntityGet<Customer> | null>(null);
    const [smsDraft, setSmsDraft] = useState<SmsDraft | undefined>();
    const draft = (customerData as { smsDraft?: SmsDraft } | undefined)?.smsDraft;

    const handleCloseChat = useCallback(
        () => {
            closeChat();
            setActiveCustomer(null);
            setSmsDraft(undefined);
        },
        [closeChat],
    );

    const handleBack = useCallback(() => {
        setSmsDraft(undefined);
        setActiveCustomer(null);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSmsDraft(undefined);
            return;
        }
        if (customerData?.id) {
            setActiveCustomer(customerData as unknown as EntityGet<Customer>);
            setSmsDraft(draft);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, customerData?.id, draft]);

    if (!isOpen) return null;

    if (!isSMSEnabled) {
        return (
            <SMSChatLayout
                customerData={customerData as unknown as EntityGet<Customer>}
                onRequestClose={handleCloseChat}
                hideInput
            >
                <EmptyMessageContainer>
                    <EmptyMessageEmoji>
                        💬
                    </EmptyMessageEmoji>
                    <EmptyMessageTitle>
                        Need to send SMS?
                    </EmptyMessageTitle>
                    <EmptyMessage message="This feature isn't activated yet. Contact our friendly support team to get started!" />
                </EmptyMessageContainer>
            </SMSChatLayout>
        );
    }

    return (
        <>
            <div hidden={!!activeCustomer}>
                <ConversationsList
                    onRequestClose={handleCloseChat}
                    onCustomerSelect={(customer) => {
                        setSmsDraft(undefined);
                        setActiveCustomer(customer);
                    }}
                />
            </div>
            {activeCustomer && (
                <SMSChat
                    onBackClick={handleBack}
                    customerData={activeCustomer as unknown as EntityGet<Customer>}
                    onRequestClose={handleCloseChat}
                    smsDraft={
                        activeCustomer.id === customerData?.id
                            ? smsDraft
                            : undefined
                    }
                />
            )}
        </>
    );
};

export default SMSChatModal;
